/* System defined core library */
import { HttpClient, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { BootstrapParams } from '../../../../bootstrap-params';

/* User defined library/components */
import { SelectedExamDetailService } from '../../../scheduling/shared/services/selected-exam-detail.service';
import { CultureService } from '../../services/culture.service';

/* User defined enums/constants */
import * as constants from '../../constants/scheduling.constant';


@Component({
  selector: ' app-geo-locator',
  templateUrl: './geo-locator.component.html'
})
export class GeoLocatorComponent implements OnInit, OnDestroy {

  /* User defined private/public variables  */
  public dataSource: Observable<any>;
  public typeaheadLoading: boolean;
  public typeaheadNoResults: boolean=false;

  /**    
   * Typeahead text input value.  Gets set to formatted_address on successful selection.
   * */
  public selectedLocation: string;

  private address = '';
  private bingCountryFilterMaxResults = 10;
  private bingLocationApiUrl = constants.BingLocationApiURL;
  private selectedLanguageCode: string;
  private url = constants.GoogleApiURL;

  languageCode$: Subscription;

  @ViewChild('locationInput') vc: any;

  /* Input-OutPut parameters decalartions */
  @Input() geolocation: any[];
    @Output() geolocationChange = new EventEmitter<any[]>();
    @Output() errorText = new EventEmitter<any[]>();
  @Input() public placeHolderText: string = constants.LocationPlaceHolderText;
  @Input() displayLabel: boolean = true;

  /* Constructor declaration with required injected services */
  public constructor(
    private bootstrapParams: BootstrapParams,
    private cultureService: CultureService,
    private http: HttpClient,
    private selectedExamDetailService: SelectedExamDetailService
  ) {

    this.dataSource = new Observable(
      (observer: any) => {
        observer.next(this.selectedLocation);
      })
      .pipe(mergeMap((token: string) => this.getLocation(token)));

    // language selected by user from dropdown
    this.languageCode$ = this.selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        this.selectedLanguageCode = languageCode;
      });
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    this.address = this.bootstrapParams.googleRegion;
    if (this.placeHolderText != null && this.placeHolderText.trim() != constants.LocationPlaceHolderText) {
      this.selectedLocation = this.placeHolderText;
      //Forcefully calling google geolocation api if provision is already selected
      this.searchAndSelect(this.selectedLocation);
    }
    if (this.vc) {//set focus on input field
      setTimeout(() => {
        this.vc.nativeElement.focus();
      }, 100);
    }
  }

  ngOnDestroy() {
    this.languageCode$.unsubscribe();
  }

  /* Emit the selected location to parent */
    public searchAndSelect(token: string): void {
    if (token !== "") {//do check for token is not empty
      this.getLocation(token).subscribe((locResults) => {
        if (locResults.length) {
          this.geolocation = [
            locResults[0].geometry.location.lat,
            locResults[0].geometry.location.lng,
            locResults[0].formatted_address
          ];
          this.selectedLocation = locResults[0].formatted_address;
        }
        else {
          this.typeaheadNoResults = true;
          this.geolocation = null;
        }
        this.errorText.emit([{ "typeaheadNoResults": this.typeaheadNoResults }]);
          

          this.geolocationChange.emit(this.geolocation);
         
      });
    }
  }

  /* Emit the selected location to parent */
  public typeaheadOnSelect(e: TypeaheadMatch): void {
    this.geolocation = [
      e.item.geometry.location.lat,
      e.item.geometry.location.lng,
      e.item.formatted_address
    ];
      this.errorText.emit([{ "typeaheadNoResults": this.typeaheadNoResults }]);
      this.geolocationChange.emit(this.geolocation);     
  }

  public changeTypeaheadLoading(e: boolean): void {
      this.typeaheadLoading = e;
      if (this.selectedLocation == '')
          this.geolocationChange.emit(null); 
  }

  public changeTypeaheadNoResults(e: boolean): void {
      this.typeaheadNoResults = e;
      this.errorText.emit([{ "typeaheadNoResults": this.typeaheadNoResults }]);

  }

  /* Get location from google API */
  public getLocation(token: string): Observable<any> {

    // if using bing maps, use bing maps location API as well
    if (this.bootstrapParams.useBingMaps) {

      // token encoded for multi-byte
      // wildcard search is now baked into the api call so we don't need to add a '%' to the query string - KJC
      let URL = this.bingLocationApiUrl + `?q=${encodeURIComponent(token)}&key=${this.bootstrapParams.bingMapKey}`;


      // language filter
      if (this.selectedLanguageCode) {
        URL += `&c=${this.cultureService.mapLanguageCodeToISO(this.selectedLanguageCode)}`;
      }

      // if country filter specified, expand the results being returned
      if (this.bootstrapParams.bingMapCountryFilter) {
        URL += `&maxResults=${this.bingCountryFilterMaxResults}`;
      }

      return this.http.get(URL)
        .pipe(map((res: HttpRequest<any>) => {
          let body: any = res;
          let results: any[] = [];
          if (body && body.resourceSets) {
            body.resourceSets.forEach((rs: any) => {

              if (rs.resources) {
                let filteredResources: any[] = rs.resources.slice(0);

                // filter by country if set in config
                if (this.bootstrapParams.bingMapCountryFilter) {
                  filteredResources = rs.resources.filter((x: any) => x.address && x.address.countryRegion && x.address.countryRegion === this.bootstrapParams.bingMapCountryFilter);
                }

                // Bug 41800: post-filter, take 1 result to support "tab" and "blur" workaround
                filteredResources = filteredResources.slice(0, 1);

                // convert to geometry obj to be compatabile with google
                filteredResources.forEach((r: any) => {
                  results.push({
                    geometry: {
                      location: {
                        lat: r.point.coordinates[0],
                        lng: r.point.coordinates[1]
                      }
                    },
                    formatted_address: this.formatBingResource(r)
                  });
                });
              }
            });
            return results;
          }
          return null;
        }));
    }
    // google maps location api
    else {
      let queryParams: HttpParams = new HttpParams().set('v', '3.exp').set('key', this.bootstrapParams.googleMapKey).set('address', token);
      const options: HttpRequest<any> = new HttpRequest<any>('GET', null, queryParams);
      let URL = this.address ? this.url + `?region=${this.address}&components=country:IE` : this.url;
      return this.http.get(URL, { params: queryParams })
        .pipe(map((res: HttpResponse<any>) => {
          let body: any = res;
          if (body) {
            return body.results;
          }
          return null;
        }));
    }
  }

  /**
   * formatResource
   * @description formats resource based on type returned
   * @param resource
   */
  formatBingResource(resource: any) {
    if (!resource) return '';

    // default value
    let formattedValue = resource.name;

    // entity types: https://docs.microsoft.com/en-us/bingmaps/v8-web-control/modules/spatial-data-service-module/getboundaryrequestoptions-object
    if (resource && resource.address && resource.entityType) {

      let addressParts: string[] = [];

      // postcode1-3 types for zip code searches
      if (resource.entityType.indexOf('Postcode') !== -1) {
        if (resource.address.locality) addressParts.push(resource.address.locality);
        if (resource.address.adminDistrict) addressParts.push(resource.address.adminDistrict);
        if (resource.address.postalCode) addressParts.push(resource.address.postalCode);
        if (resource.address.countryRegion) addressParts.push(resource.address.countryRegion);
      }

      // custom format based on parts
      if (addressParts.length > 0) {
        formattedValue = addressParts.join(', ');
      }
    }

    return formattedValue;
  }
}
